import React from 'react';
import PropTypes from 'prop-types';
import AmendButton from 'components/button/AmendButton';
import BookingPhaseContainer from './phase/BookingPhaseContainer';
import BookingAmendInformation from './BookingAmendInformation';
import BookingAmendLoader from './BookingAmendLoader';
import Dialog from 'material-ui/Dialog';
import RaisedButton from 'material-ui/RaisedButton';
import authHelper from 'helpers/auth';
import { connect } from 'react-redux';
import compose from 'recompose/compose';
import fetchResourceById from 'components/hoc/fetchResourceById';
import translate from 'components/hoc/translate';
import { getFormValues, reduxForm } from 'redux-form';
import { amendBooking } from 'store/actions/bookings/amendBookings';
import { createBookingAmendTransactionRequest, fetchBookingAmendPriceRequest } from 'http/requests/bookings/amendBookingsRequests';
import { createTransactionInvoiceRequest } from 'http/requests/transactionRequests';
import { fetchBooking } from 'store/actions/bookings/fetchBookings';
import { fetchOrderBookingActivities } from 'store/actions/orderActivitiesActions';
import { isDeferred, isInvoiced } from 'helpers/booking';
import { showNotification } from 'store/actions/uiActions';
import utils from 'helpers/utils';
import styled from 'styled-components';
import { pick } from 'lodash';

const StyledWrapper = styled.div`
    position: relative;
    display: flex;
    height: 100%;
    min-height: 400px;
    height: 400px;
    flex-grow: 1;
    overflow-y: scroll;
    overflow-x: none;

    ${ (props) => {
        if (props.loading) {
            return `
                pointer-events: none;
                user-select: none;
            `;
        }
    } }
`;

class BookingAmendContainer extends React.Component {
    constructor(props, context) {
        super(props, context);

        this.state = {
            loading: false,
            message: null,
            phase: null,
            prices: null,
            selectedQuote: null,
            showDialog: false,
        };
    }

    componentDidMount() {
        this.setState({
            phase: 'form',
        });
    }

    componentDidUpdate(prevProps) {
        const {
            formValues,
        } = this.props;

        if (!utils.isEmpty(formValues) && !utils.isEmpty(utils.diffObjects(formValues, prevProps.formValues))) {
            this.resetState();
        }
    }

    resetState = () => {
        this.setState({
            loading: false,
            message: null,
            paymentLink: null,
            phase: 'form',
            prices: null,
            quotes: null,
            selectedQuote: null,
        });
    }

    openDialog = () => {
        this.setState({
            phase: 'form',
            showDialog: true,
        });
    }

    closeDialog = () => {
        this.setState({
            loading: false,
            message: null,
            paymentLink: null,
            phase: null,
            prices: null,
            quotes: null,
            selectedQuote: null,
            showDialog: false,
        });
    }

    setLoadingOn = () => {
        this.setState({
            loading: true,
        });
    }

    setLoadingOff = () => {
        this.setState({
            loading: false,
        });
    }

    setPhase = (phase) => {
        this.setState({
            phase: phase,
        });
    }

    handleSave = () => {
        const {
            dispatchActivities,
            dispatchForm,
            dispatchNotification,
            formValues,
            id,
            translate,
        } = this.props;

        return new Promise((resolve, reject) => {
            dispatchForm({
                onError: () => {
                    // This location should have where we bounce the error back to the form with SubmissionError.
                    this.setMessage(translate('resources.bookings.amend.messages.amendError'));
                    reject();
                },
                onSuccess: () => {
                    // When the user is not an admin we will resolve the function.
                    if (authHelper.isAdmin()) {
                        // Update the booking activies.
                        dispatchActivities({
                            id: id,
                        });
                    }

                    dispatchNotification(translate('notifications.orders.update.success'));

                    resolve(this.closeDialog);
                },
                id: id,
                values: formValues,
            });
        });
    }

    handlePrices = () => {
        const {
            data: booking,
            formValues,
            id,
            translate,
        } = this.props;

        const params = { ...booking, ...formValues };

        this.setLoadingOn();

        // Make sure we only send the parts of the booking we need
        const requestData = pick(params, [
            'destination_address',
            'destination_arrival_date',
            'destination_departure_date',
            'number_of_persons',
            'origin_address',
            'price',
        ]);

        return fetchBookingAmendPriceRequest(id, requestData)
            .then((response) => {
                // Remove when this is received from request
                response.difference = response.price;

                setTimeout(() => {
                    this.setState({
                        prices: response,
                    }, () => {
                        if (isDeferred(booking)) {
                            this.setState({
                                loading: false,
                                message: translate('resources.bookings.amend.messages.payment-not-required'),
                                phase: 'payment-not-required',
                            });
                        } else {
                            this.handlePhaseTransactions();
                        }
                    });
                }, 2000);
            })
            .catch((error) => {
                const { message } = error.response;

                this.setState({
                    loading: false,
                    phase: 'price-check-error',
                    message: translate(`resources.bookings.amend.messages.${ message }`),
                });

            });
    }

    handleInvoiceTransaction = () => {
        const {
            dispatchNotification,
            translate,
        } = this.props;

        const {
            transactionId,
        } = this.state;

        this.setState({
            loading: true,
        });

        return createTransactionInvoiceRequest(transactionId)
            .then(() => {
                this.setState({
                    loading: false,
                });

                dispatchNotification(translate('notifications.orders.update.success'));
            })
            .catch((error) => {
                this.setState({
                    message: translate('resources.bookings.amend.messages.amendError'),
                    loading: false,
                });

                dispatchNotification(error.response.message, 'warning');
            });
    }

    handlePhaseTransactions = () => {
        const {
            data: booking,
            formValues,
            id,
            translate,
        } = this.props;
        const { price } = this.state.prices;

        const params = { ...formValues, price: price };

        this.setLoadingOn();

        return createBookingAmendTransactionRequest(id, params)
            .then((transaction) => {
                const {
                    id: transactionId,
                    policies: {
                        confirmable: {
                            allowed: isConfirmable,
                        },
                        invoiceable: {
                            allowed: isInvoiceable,
                        },
                    },
                } = transaction;

                let phase = this.state.phase;

                if (isConfirmable) {
                    phase = 'payment-not-required';
                } else if (isInvoiced(booking) && isInvoiceable) {
                    phase = 'payment-invoice';
                } else {
                    phase = 'payment-required';
                }

                this.setState({
                    message: translate(`resources.bookings.amend.messages.${ phase }`),
                    transactionId,
                }, () => {
                    setTimeout(() => {
                        this.setState({
                            phase,
                            loading: false,
                        });
                    }, 2000);
                });
            })
            .catch((error) => {
                const { message } = error;

                this.setState({
                    loading: false,
                    message: translate(`resources.bookings.amend.messages.${ message }`),
                });
            });
    }

    setPhase = (phase) => {
        this.setState({
            phase: phase,
        });
    }

    setMessage = (message) => {
        this.setState({
            message: message,
        });
    }

    renderButtons = () => {
        const {
            translate,
        } = this.props;

        const {
            phase,
        } = this.state;

        const closeButton = <RaisedButton
            key='close'
            keyboardFocused={ false }
            label={ translate('actions.close') }
            onTouchTap={ this.closeDialog }
            style={ { marginRight: '20px' } }
        />;

        const saveButton = <RaisedButton
            key='save'
            keyboardFocused={ true }
            label={ translate('actions.save') }
            onTouchTap={ this.handleSave }
            primary
        />;

        const invoicePaymentButton = <RaisedButton
            key='save'
            keyboardFocused={ true }
            label={ translate('resources.bookings.amend.buttons.paymentInvoice') }
            onTouchTap={ this.handleInvoiceTransaction }
            primary
        />;

        const paymentButton = <RaisedButton
            key='save'
            keyboardFocused={ true }
            label={ translate('resources.bookings.amend.buttons.paymentMethod') }
            onTouchTap={ () => {
                this.setPhase('payments');
            } }
            primary
        />;

        const priceButton = <RaisedButton
            key='save'
            keyboardFocused={ true }
            label={ translate('resources.bookings.amend.buttons.price') }
            onTouchTap={ this.handlePrices }
            primary
        />;

        switch (phase) {
            case 'payment-invoice':
                return [
                    closeButton,
                    invoicePaymentButton,
                ];
            case 'payment-required':
                return [
                    closeButton,
                    paymentButton,
                ];
            case 'payment-not-required':
                return [
                    closeButton,
                    saveButton,
                ];
            case 'form':
                return [
                    closeButton,
                    priceButton,
                ];
            case 'payment-link':
            case 'payments':
            case 'price-check-error':
            default:
                return [
                    closeButton,
                ];
        }
    }

    render() {
        const {
            data,
            form,
            id,
            translate,
        } = this.props;

        const {
            loading,
            message,
            phase,
            prices,
            showDialog,
            transactionId,
        } = this.state;

        return (
            <div>
                <AmendButton onClick={ this.openDialog } />

                <Dialog
                    actions={ this.renderButtons() }
                    modal={ false }
                    onRequestClose={ this.closeDialog }
                    overlayStyle={ {
                        backgroundColor: 'rgba(237, 236, 236, 0.6)',
                    } }
                    open={ showDialog }
                    title={ translate('resources.bookings.amend.title') }
                >
                    <StyledWrapper loading={ loading }>
                        <BookingPhaseContainer
                            data={ data }
                            dispatchLoaderOff={ this.setLoadingOff }
                            dispatchLoaderOn={ this.setLoadingOn }
                            dispatchMessage={ this.setMessage }
                            form={ form }
                            id={ id }
                            loading={ loading }
                            phase={ phase }
                            prices={ prices }
                            setPhase={ this.setPhase }
                            transactionId={ transactionId }
                        />

                        { message &&
                            <BookingAmendInformation
                                message={ message }
                            />
                        }
                    </StyledWrapper>

                    { loading &&
                        <BookingAmendLoader
                            phase={ phase }
                        />
                    }
                </Dialog>
            </div>
        );
    }
}

BookingAmendContainer.propTypes = {
    data: PropTypes.object,
    dispatchActivities: PropTypes.func.isRequired,
    dispatchNotification: PropTypes.func.isRequired,
    form: PropTypes.string.isRequired,
    formValues: PropTypes.object,
    id: PropTypes.number,
    dispatchForm: PropTypes.func.isRequired,
    translate: PropTypes.func,
};

const mapStateToProps = (state) => {
    return {
        quotes: state.transactions.quotes,
        formValues: getFormValues('bookings_amend')(state),
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        dispatchForm: (values) => {
            dispatch(amendBooking(values));
        },
        dispatchActivities: (values) => {
            dispatch(fetchOrderBookingActivities(values));
        },
        dispatchNotification: (text, type) => {
            dispatch(showNotification(text, type));
        },
    };
};

const enhance = compose(
    translate,
    reduxForm({
        enableReinitialize: true,
        form: 'bookings_amend',
        keepDirtyOnReinitialize: true,
    }),
    connect(
        mapStateToProps,
        mapDispatchToProps,
    ),
    fetchResourceById({
        action: fetchBooking,
        resource: 'bookings',
    })
);

export default enhance(BookingAmendContainer);



// WEBPACK FOOTER //
// ./src/pages/bookings/amend/BookingAmendContainer.js